import { useRef, useState, type MouseEvent } from "react";
import { Button, ChatMessageList, EmptyState, RichComposer, type Mention, type MessageActions, type RichComposerHandle } from "@omnabase/ui";
import type { AttachmentOut, ChatEvent } from "@omnabase/shared";
import { useSession } from "../../shell/store";
import { useNavigate, useWs } from "../../lib/nav";
import { useCan } from "../../lib/access";
import { deleteChat, editChat } from "../../lib/chat";
import { attachWithinLimits, toAttachment } from "../../lib/inbox";
import { canDeleteMessage, canEditMessage, chatAgents, keptMentions, toKitMessage } from "../../lib/chatRooms";
import { mentionIds } from "../../lib/mentions";
import type { TicketDiscussion } from "./useTicketDiscussion";

/** The side panel's Discussion tab (the ticket discussion round, 2026-11-02; brief rule 5): the kit's chat in its narrow variant (it scrolls
 *  its own list, never the ticket's thread), the composer with mentions and files, edit and delete as in a channel, Follow / Stop following
 *  once the room exists, and the empty sentence with the composer ready before the first line. The data is `useTicketDiscussion`'s. The
 *  discussion polish round (2026-11-02): the info body's 24 px sides, no hint under the box, the member's AI's byline on its lines. The
 *  composer-and-fixes round (2026-11-03): the one place to write a line; the box is named "Discussion", Escape takes the cursor out of it (a
 *  second Escape closes the drawer, the inbox's rule) and the page's `focusSignal` puts the cursor in. */
export const DISCUSSION_EMPTY = "Nobody has said anything about this ticket yet.";

export function DiscussionPanel({ discussion, mentions, focusSignal }: { discussion: TicketDiscussion; /** whom the @ list offers before the room exists: the colleagues who may see the ticket */ mentions: Mention[];
  /** the composer-and-fixes round (2026-11-03; brief rule 6): Discuss, D, /discuss and the condensed item put the cursor in the box (a counter) */ focusSignal?: number }) {
  const { workspace, me, toast } = useSession();
  const { slug, ws } = useWs();
  const navigate = useNavigate();
  const team = useCan()("team");
  const composer = useRef<RichComposerHandle>(null);
  const [held, setHeld] = useState<AttachmentOut[]>([]);
  const { room } = discussion;
  const members = workspace.members, myId = me.user.id;
  const fail = (e: unknown) => toast((e as Error).message, { kind: "error" });
  const messages = discussion.events.filter((e) => e.type === "message").map((e) => toKitMessage(e, room?.id ?? "", members, (n) => ws(`/tickets/${n}`)));
  const offered = room ? members.filter((m) => m.status === "active" && m.userId !== myId && (room.memberIds ?? []).includes(m.userId)).map((m) => ({ id: m.userId, name: m.name, initials: m.initials })) : mentions.filter((m) => m.id !== myId);

  const send = async () => {
    const c = composer.current; if (!c) return;
    const text = c.getText().trim(); if (!text && !held.length) return;
    const ids = mentionIds(c.getHTML()), sending = held;
    c.clear(); setHeld([]);
    try { await discussion.send(text, ids, sending.map((f) => f.id)); } catch (e) { fail(e); c.insertText(text); setHeld((cur) => [...sending, ...cur]); }
  };
  const actions: MessageActions | undefined = room ? {
    canEdit: (m) => canEditMessage(m, myId),
    canDelete: (m) => canDeleteMessage(m, room, myId, team),
    onEdit: (id, body) => void editChat(slug, room.id, id, body, keptMentions(discussion.events.find((e: ChatEvent) => e.id === id), body, members)).then(() => discussion.reload(), fail),
    onDelete: (id) => void deleteChat(slug, room.id, id).then(() => discussion.reload(), fail),
  } : undefined;
  // a ticket chip opens the ticket inside the app (the kit draws plain links)
  const openTicket = (e: MouseEvent<HTMLDivElement>) => {
    const a = (e.target as HTMLElement).closest?.("a.ob-chat__ticket"); if (!a || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault(); navigate(a.getAttribute("href")!);
  };
  const empty = discussion.loaded ? <EmptyState icon="chat" title={DISCUSSION_EMPTY} text="Only the team sees this. Mention a colleague with @ to bring them in." /> : null;
  return (
    <div className="ob-discussion" aria-label="Discussion" onClick={openTicket}>
      <FollowBar discussion={discussion} fail={fail} />
      <ChatMessageList narrow messages={messages} agents={chatAgents(members)} me={myId} empty={empty} actions={actions} />
      <RichComposer ref={composer} variant="chat" plain placeholder="Write to the team…" mode="reply" label="Discussion" escapeBlurs focusSignal={focusSignal} sendHint={null /* the discussion polish round (2026-11-02): no hint under the box; Enter sends, Shift+Enter breaks the line */}
        mentions={offered} onSend={() => void send()} attachments={held.map((f) => toAttachment(f))} onRemoveAttachment={(i) => setHeld((cur) => cur.filter((_, n) => n !== i))}
        onAttach={(picked) => void attachWithinLimits(held, picked, discussion.upload, (a) => setHeld((cur) => [...cur, a]), (t) => toast(t, { kind: "error" }))} />
    </div>
  );
}

/** the line above the messages: who hears about it, with Follow / Stop following once the discussion exists */
function FollowBar({ discussion, fail }: { discussion: TicketDiscussion; fail: (e: unknown) => void }) {
  const { room } = discussion;
  if (!room) return <div className="ob-discussion__bar"><span className="ob-discussion__bar-text">Colleagues you mention follow the discussion, and so does whoever holds the ticket.</span></div>;
  const on = !!room.following;
  return (
    <div className="ob-discussion__bar">
      <span className="ob-discussion__bar-text">{on ? "You follow this discussion." : "You do not follow this discussion."}</span>
      <Button size="sm" onClick={() => void discussion.follow(!on).catch(fail)}>{on ? "Stop following" : "Follow"}</Button>
    </div>
  );
}
